import React, { PureComponent, Fragment } from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import Translator from 'translator';
import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { Menu, withStyles } from '@material-ui/core';
import { Build as ActionIcon, Refresh as RefreshIcon, Add as AddIcon, RemoveRedEye as ViewIcon } from '@material-ui/icons';
import { FHETable } from 'data/components';
import { Toastr, tools } from 'data/common';
import renderEnvComponent from 'data/common/renderEnvComponent';
import { getHomes } from '../../data/Homes/actions';
import ActionsMenu from './components/ActionsMenu';
import getSelectors from './selectors';

class Home extends PureComponent {
    static propTypes = {
        getHomes: PropTypes.func.isRequired,
        Homes: PropTypes.object.isRequired,
        managements: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        profile: PropTypes.object.isRequired,
        initialized: PropTypes.bool.isRequired,
        classes: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);
        const { initialized } = props;

        this.state = {
            loading: !initialized,
            openedMenu: false,
            boxMac: null,
            target: null
        };
    }

    componentDidMount() {
        const { initialized } = this.props;
        if (!initialized) {
            this.refreshHomes();
        }
    }

    closeMenuState = () => {
        this.setState({ openedMenu: false });
    };

    refreshHomes = () => {
        const { getHomes } = this.props;
        this.setState({ loading: true }, () => {
            return getHomes()
                .catch(err => {
                    const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                    Toastr.error(message);
                })
                .finally(() => {
                    this.setState({ loading: false });
                });
        });
    };

    navigateToDetail = rowData => {
        const { history } = this.props;
        return history.push({ pathname: `home/detail/${rowData.homeId}` });
    };
    navigateToAdd = () => {
        const { history } = this.props;
        return history.push({ pathname: 'home/add' });
    };
    hasRightToAction = () => {
        const { profile, history } = this.props;
        const userRights = profile.getIn(['user_rights', history.environment], []);
        const rightsAction = [
            `homeDelete_${history.environment}`,
            `updateBox_${history.environment}`,
            `refreshBox_${history.environment}`,
            `restartBox_${history.environment}`,
            'startVpn',
            'stopVpn'
        ];
        return profile.get('admin') || userRights.some(v => rightsAction.includes(v));
    };

    renderActions = memoizeOne((profile, Homes) => {
        const { history } = this.props;

        let actions = {
            row: [
                {
                    icon: ActionIcon,
                    tooltip: Translator.translate(`generic.home.boxActions`, Translator.translate(`${history.environment}.centerName`)),
                    hasRight: this.hasRightToAction,
                    onClick: ({ macAddress, homeId, boxId }, callbackRowActionLoading, event) => {
                        this.setState({
                            target: event.currentTarget,
                            callbackRowActionLoading,
                            openedMenu: true,
                            boxMac: macAddress,
                            homeId,
                            boxId: boxId || 0
                        });
                    }
                }
            ],
            toolbar: [
                {
                    icon: RefreshIcon,
                    tooltip: Translator.translate(`generic.home.table.globalAction.refresh.tooltip`),
                    onClick: this.refreshHomes
                }
            ]
        };
        if (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes('homeAdd')) {
            actions.toolbar.push({
                icon: AddIcon,
                tooltip: Translator.translate(`generic.home.table.globalAction.add.tooltip`),
                onClick: this.navigateToAdd
            });
        }
        if (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes('homeDetails')) {
            actions.row.push({
                icon: ViewIcon,
                onClick: this.navigateToDetail,
                tooltip: Translator.translate(`general.showDetail`)
            });
        }
        return actions;
    });

    renderData = memoizeOne((Homes, managements) => {
        let result = [];
        Homes.map((Home, HomeId) => {
            const management = managements.get(Home.getIn(['managementId'], '').toString()) || new Map();
            result.push({
                homeId: parseInt(HomeId),
                boxId: Home.get('boxId') ? parseInt(Home.get('boxId')) : null,
                label: Home.get('label'),
                macAddress: Home.get('macAddress')
                    ? Home.get('macAddress')
                          .match(/.{1,2}/g)
                          .join(':')
                    : '',
                firmwareVersion: Home.getIn(['firmwareVersion'], null),
                boxIsConnected: Home.getIn(['boxIsConnected'], null),
                state:
                    Home.get('boxIsConnected') === null || typeof Home.get('boxIsConnected') === 'undefined'
                        ? null
                        : Home.get('boxIsConnected')
                        ? Translator.translate('general.connected')
                        : Translator.translate('general.disconnected'),
                freeSpace: Home.getIn(['freeSpace'], null),
                postalCode: management.get('buildingType') === 'apartments' ? management.get('postalCode') : Home.get('postalCode'),
                timeLag: Home.getIn(['timeDiff'], null),
                ticketsNb: Home.getIn(['ticketsNb'], 0),
                lastConnection: Home.getIn(['lastConnection'], null),
                managementLabel: management.get('libelle'),
                apNum: Home.getIn(['apNum'], null),
                upgrade: Home.get('upgrade')
            });
        });
        return result;
    });

    renderColumns = memoizeOne(profile => {
        const { classes, history } = this.props;

        return [
            {
                title: Translator.translate(`general.table.column.boxId`, Translator.translate(`${history.environment}.centerName`)),
                name: 'boxId',
                width: 90,
                fixedLeft: true
            },
            {
                title: Translator.translate('general.table.column.homeId'),
                name: 'homeId',
                width: 90,
                defaultSort: 'desc',
                fixedLeft: true
            },
            {
                title: Translator.translate('general.table.column.label'),
                name: 'label',
                action: (profile.get('admin') || profile.getIn(['user_rights', history.environment], []).includes('homeDetails')) && {
                    onClick: this.navigateToDetail,
                    tooltip: Translator.translate(`general.showDetail`)
                }
            },
            {
                title: Translator.translate('general.table.column.apNum'),
                name: 'apNum'
            },
            {
                title: Translator.translate('general.table.column.residence'),
                name: 'managementLabel'
            },
            {
                title: Translator.translate('general.table.column.macAddress'),
                name: 'macAddress',
                width: 150,
                customFilter: (value, filter) => {
                    if (!value) return false;
                    const formattedValue = value.toLowerCase().replace(/:/g, '');
                    const formattedFilter = filter.value.toLowerCase().replace(/:/g, '');
                    return formattedValue.indexOf(formattedFilter) !== -1;
                }
            },
            {
                title: Translator.translate('general.table.column.postalCode'),
                name: 'postalCode'
            },
            {
                title: Translator.translate('general.table.column.firmwareVersion'),
                name: 'firmwareVersion'
            },
            {
                title: Translator.translate('general.table.column.freeSpace'),
                name: 'freeSpace',
                render: (row, data, value) => {
                    if (!value) return null;
                    return <span className={value > 75 ? classes.lowDiskSpace : undefined}>{value}%</span>;
                }
            },
            {
                title: Translator.translate('general.table.column.timeLag'),
                name: 'timeLag'
            },
            {
                title: Translator.translate('general.table.column.ticketsNb'),
                name: 'ticketsNb',
                width: 90,
                render: (row, data, value) => {
                    if (!value) return null;
                    return <span className={value > 0 ? classes.unresolvedTickets : undefined}>{value}</span>;
                }
            },
            {
                title: Translator.translate('general.table.column.lastConnection'),
                name: 'lastConnection',
                customSort: tools.sortDate
            },
            {
                title: Translator.translate('general.table.column.state'),
                name: 'state',
                render: (row, data, value) => {
                    if (!value) return null;
                    return (
                        <span className={value === Translator.translate('general.connected') ? classes.connected : classes.disconnected}>
                            {value}
                        </span>
                    );
                }
            },
            {
                title: Translator.translate('general.table.column.upgrade'),
                name: 'upgrade',
                render: (row, data, value) => {
                    if (!Number.isInteger(value)) return null;
                    return (
                        <span className={value === 0 ? classes.disconnected : undefined}>{Translator.translate(`general.data.mode.${value}`)}</span>
                    );
                }
            }
        ];
    });

    render() {
        const { loading, openedMenu, target, boxMac, callbackRowActionLoading, homeId, boxId } = this.state;
        const { profile, Homes, managements } = this.props;

        return (
            <Fragment>
                {openedMenu && (
                    <Menu anchorEl={target} keepMounted open={openedMenu} onClose={this.closeMenuState} onClick={this.closeMenuState}>
                        <div>
                            <ActionsMenu boxMac={boxMac} homeId={homeId} boxId={boxId} handleLoading={callbackRowActionLoading} />
                        </div>
                    </Menu>
                )}
                <FHETable
                    idTable="homeList"
                    columns={this.renderColumns(profile)}
                    data={this.renderData(Homes, managements)}
                    actions={this.renderActions(profile, Homes)}
                    title={Translator.translate(`generic.home.table.title`)}
                    emptyDataSourceMessage={Translator.translate(`generic.home.table.body.emptyDataSourceMessage`)}
                    isLoading={loading}
                />
            </Fragment>
        );
    }
}

const styles = theme => ({
    lowDiskSpace: {
        color: theme.palette.error.main
    },
    checkIcon: {
        color: theme.palette.success.main
    },
    freeSpaceBarRoot: {
        height: 10
    },
    connected: {
        color: theme.palette.success.main
    },
    disconnected: {
        color: theme.palette.error.main
    },
    unresolvedTickets: {
        color: theme.palette.error.main
    }
});

function mapStateToProps(state, props) {
    const { history } = props;
    const selectors = getSelectors(history.environment);
    return {
        profile: selectors.getProfile(state),
        Homes: selectors.getHomesWithBoxes(state),
        initialized: selectors.getHomesInitialized(state),
        brands: selectors.getBrands(state),
        managements: selectors.getManagements(state)
    };
}

export default withRouter(
    renderEnvComponent(
        'scenes/Home/index',
        connect(
            mapStateToProps,
            (dispatch, ownProps) => bindActionCreators(tools.mergePropsToAction(ownProps.history.environment, { getHomes }), dispatch)
        )(withStyles(styles, { withTheme: true })(Home))
    )
);



// WEBPACK FOOTER //
// ./environments/genericEnv/scenes/Home/index.js